import * as logger from 'loglevel'
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, Navigate, useLocation } from 'react-router-dom'
import ROUTES from '../../routesConfig'

import { Box } from '@mui/material'

import anearApi from '../Api/AnearApi'

import UIStateContainer from '../Containers/UIStateContainer'
import useNavigation from '../Utils/navigationUtils'
import UserAuthContainer from '../Containers/UserAuthContainer'
import AuthTokenExpiredError from '../Api/AuthTokenExpiredError'
import EventFrame from './EventFrame'
import CssLoader from '../Utils/CssLoader'
import EventOverPage from './EventOverPage'
import EventPausedPage from './EventPausedPage'
import EventConfirmExitDialog from '../UI/EventConfirmExitDialog'
import { clearDocumentTitleLayer, setDocumentTitleLayer } from '../Utils/documentTitle'

const ParticipantExitType = 'PARTICIPANT_EXIT'
const EventUnmountMessageType = 'EVENT_UNMOUNT'
const DeadRouteStates = ['closed', 'archive', 'canceled']
const HostControlAutoJoinStates = ['review', 'reward', 'concluded']

const isDeadRouteState = (state) => DeadRouteStates.includes(String(state || '').toLowerCase())

/** ANAPI typed error when event.app_version is not patch-compatible with the active AppM. */
export const isAppVersionIncompatibleError = (error) => {
  if (!error) return false
  const title = error?.error?.title || error?.json_errors?.title || error?.title
  if (title === 'App Version Incompatible') return true
  const message = error?.message || error?.error?.detail || ''
  return typeof message === 'string' && message.includes('not compatible with the current runtime')
}

/** Presence url context from /e/:slug query string only (no path-based deep links). */
export const buildEventUrlContext = (search = '') => {
  const params = new URLSearchParams(search || '')
  const queryEntries = Object.fromEntries(params.entries())
  if (Object.keys(queryEntries).length === 0) return null
  return queryEntries
}

const useEvent = (slug, ui, handleAuthTokenExpired, userAuthContainer) => {
  const [anearEvent, setAnearEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { startProgress, progressComplete } = ui

  useEffect(() => {
    let cancelled = false

    const fetchEvent = async () => {
      try {
        startProgress()

        const ae = await anearApi.getEvent(slug)

        if (!cancelled) {
          logger.info(
            `Fetched event '${slug}' – spectators ${
              ae.spectatorsAllowed() ? 'allowed' : 'NOT allowed'
            }`
          )
          // Only init messaging for non-terminated events
          if (!ae.isOver()) {
            await ae.initMessaging()
          }
          setAnearEvent(ae)
        }
      } catch (err) {
        const status = err?.status
        // 404 / app_version_incompatible are expected user-facing outcomes (EventOverPage),
        // not exceptional runtime failures.
        if (status === 404) {
          logger.info(`Event '${slug}' not found (404)`)
        } else if (isAppVersionIncompatibleError(err)) {
          logger.info(`Event '${slug}' app version incompatible`)
        } else {
          logger.error(err)
        }
        if (!cancelled) {
          if (err instanceof AuthTokenExpiredError) {
            handleAuthTokenExpired(err, userAuthContainer)
          }
          setError(err)
        }
      } finally {
        if (!cancelled) setLoading(false)
        progressComplete()
      }
    }

    fetchEvent()

    return () => { cancelled = true } // eslint-disable-next-line
  }, [slug])

  // reload when called (e.g. on transition)
  const reloadEvent = useCallback(async () => {
    if (!anearEvent) return

      try {
        startProgress()

        await anearEvent.closeMessaging()
        const refreshedEvent = await anearApi.getEvent(anearEvent.slug)

        logger.info(
          `Reloaded event '${anearEvent.slug}' – spectators ${
            refreshedEvent.spectatorsAllowed() ? 'allowed' : 'NOT allowed'
          }`
        )

        await refreshedEvent.initMessaging()
        setAnearEvent(refreshedEvent)
      } catch (err) {
        logger.error('Failed to reload event', err)
        if (err instanceof AuthTokenExpiredError) {
          handleAuthTokenExpired(err, userAuthContainer)
        }
      } finally {
        progressComplete()
      } // eslint-disable-next-line
  }, [anearEvent])

  return { anearEvent, loading, reloadEvent, error }
}

const useParticipant = (anearEvent, urlContext, ui, handleAuthTokenExpired, userAuthContainer) => {
  const [anearParticipant, setAnearParticipant] = useState(null)
  const [loading, setLoading] = useState(true)
  const { startProgress, progressComplete } = ui

  useEffect(() => {
    if (!anearEvent) return

    const shouldLoad =
      anearEvent.isParticipant() ||
      (anearEvent.isCreator() && !HostControlAutoJoinStates.includes(anearEvent.state))

    if (!shouldLoad) {
      setLoading(false)
      setAnearParticipant(null)
      return
    }

    let cancelled = false

    const fetchParticipant = async () => {
      try {
        startProgress()

        const participant = await anearApi.getEventParticipant(anearEvent.id)

        if (!cancelled) {
          participant.setUrlContext(urlContext)
          await participant.initMessaging()
          setAnearParticipant(participant)
        }
      } catch (err) {
        logger.error(err)
        if (err instanceof AuthTokenExpiredError) {
          handleAuthTokenExpired(err, userAuthContainer)
        }
      } finally {
        if (!cancelled) setLoading(false)
        progressComplete()
      }
    }

    fetchParticipant()

    return () => { cancelled = true } // eslint-disable-next-line
  }, [anearEvent, urlContext])

  const becomeParticipant = useCallback(async () => {
    if (!anearEvent) return

      try {
        startProgress()
        const participant = await anearApi.becomeEventParticipant(anearEvent)
        participant.setUrlContext(urlContext)
        await participant.initMessaging()
        // Important: close the spectators channel immediately on join to avoid a race where
        // spectator renders (triggered by the JOIN's PARTICIPANT_ENTER) can overwrite the
        // newly-joined participant's private display before EventFrame switches subscriptions.
        try {
          await anearEvent.closeSpectatorsChannel?.()
        } catch (_e) {
          // Best-effort: spectator channel might not be attached yet.
        }
        setAnearParticipant(participant)
        return participant
      } catch (err) {
        logger.error(err)
        if (err instanceof AuthTokenExpiredError) {
          handleAuthTokenExpired(err, userAuthContainer)
        } else {
          throw err
        }
      } finally {
        progressComplete()
      } // eslint-disable-next-line
  }, [anearEvent, urlContext])

  const clearParticipant = useCallback(() => {
    setAnearParticipant(null)
  }, [])

  return { anearParticipant, loading, becomeParticipant, clearParticipant }
}

const EventPage = () => {
  //   - render outer event container with exit controls
  //   - subscribes to eventsChannel broadcast and transition message types
  //   - handler for event transition triggered by external App
  //   - handler for event broadcast messages (Not yet functional)
  //   - click handlers for events that could come from within EventFrame:
  //      - JOIN   become participant)
  //      - BACK   exit the event as participant or spectator
  //      - REPLAY play the event again when event is no longer in progress
  //   - when event is requested to play again by a prev participant, handles
  //     cloning event
  //   - cleanup when unmounting, handles detach of messaging
  //       for the participant and Event
  //
  const { navigateToHome, navigateToPathWithReplace, handleAuthTokenExpired } = useNavigation()
  const ui = UIStateContainer.useContainer()
  const userAuthContainer = UserAuthContainer.useContainer()
  const location = useLocation()

  const { eventSlug } = useParams()
  const urlContext = useMemo(
    () => buildEventUrlContext(location.search),
    [location.search]
  )
  const { anearEvent, loading: loadingEvent, error: eventError } =
    useEvent(eventSlug, ui, handleAuthTokenExpired, userAuthContainer)
  const { anearParticipant, loading: loadingParticipant, becomeParticipant } =
    useParticipant(anearEvent, urlContext, ui, handleAuthTokenExpired, userAuthContainer)
  const [bootReason, setBootReason] = useState(null)
  // Quiesce info populated when an EVENT_QUIESCED message arrives on the event channel.
  // Shape: null | { reason: string, reconnectLabel: string }
  const [quiesceInfo, setQuiesceInfo] = useState(null)
  const [autoJoinAttempted, setAutoJoinAttempted] = useState(false)
  const [participantError, setParticipantError] = useState(null)
  const [exitConfirmState, setExitConfirmState] = useState({
    open: false,
    prompt: null,
    stayLabel: null,
    exitLabel: null,
  })
  const subscribedRef = useRef(false);
  const participantRef = useRef(anearParticipant)
  participantRef.current = anearParticipant
  const eventRef = useRef(null)
  const cleanupRef = useRef(false)

  const setError = useState(null)[1]
  const setBroadcastMessages = useState([])[1]

  const broadcastHandler = useCallback(message => {
    setBroadcastMessages(b => [...b, message.data])
  }, [setBroadcastMessages])

  const bootParticipantHandler = useCallback(async (reason) => {
    logger.info("Participant booted with reason:", reason)
    setBootReason(reason || "You have been removed from the event.")

    try {
      // Propagate boot to all child events
      // Find all anear-app elements in the DOM (they may be in shadow DOMs)
      try {
        const anearAppElements = document.querySelectorAll('anear-app')
        for (const element of anearAppElements) {
          if (typeof element.propagateBoot === 'function') {
            await element.propagateBoot(reason)
          }
        }
      } catch (err) {
        logger.error('Error propagating boot to child events', err)
      }

      if (participantRef.current) {
        await participantRef.current.leaveEvent('BOOTED')
      }
      if (eventRef.current) {
        await eventRef.current.closeMessaging()
      }
    } catch (err) {
      logger.error('Error during boot cleanup:', err)
    }
  }, [])

  // EVENT_QUIESCED handler: tears down event/participant channels (resource conservation)
  // and renders the EventPausedPage overlay with a Rejoin button. The button reloads the
  // page, which re-enters presence on :actions and triggers ANAPI LOAD_EVENT to rehydrate
  // the AEM from its persisted snapshot. Spectators receive the same teardown but see no
  // Rejoin button (gated on participantRef.current in render). UX vocabulary stays as
  // "paused" — only the wire-layer / AEM-side concept is "quiesce".
  const eventQuiescedHandler = useCallback(async message => {
    const content = message?.data?.content || {}
    const reason = content.reason || 'Event paused for inactivity.'
    const reconnectLabel = content.reconnectLabel || 'Rejoin'
    logger.info(`Event '${anearEvent?.id}' quiesced: ${reason}`)

    try {
      const anearAppElements = document.querySelectorAll('anear-app')
      for (const element of anearAppElements) {
        if (typeof element.propagateExit === 'function') {
          try {
            await element.propagateExit('PAUSED')
          } catch (err) {
            logger.error('Error propagating pause to child event', err)
          }
        }
      }

      if (participantRef.current) {
        await participantRef.current.closeMessaging()
      }
      if (eventRef.current) {
        await eventRef.current.closeMessaging()
      }
    } catch (err) {
      logger.error('Error during quiesce teardown', err)
    }

    setQuiesceInfo({ reason, reconnectLabel })
  }, [anearEvent])

  useEffect(() => { if (anearEvent) eventRef.current = anearEvent }, [anearEvent])

  //
  // When an active event frame is on screen, gently dim the header
  // (logo + avatar) to shift focus into the "theater". When this
  // page unmounts, or the event goes away, restore the header.
  //
  // Important: we intentionally only depend on `anearEvent` here to avoid
  // an update loop caused by `ui` changing identity when its internal
  // state updates.
  //
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let dimmed = false
    if (anearEvent && !anearEvent.isOver()) {
      ui.dimHeader()
      dimmed = true
    }
    return () => {
      if (dimmed) {
        ui.undimHeader()
      }
    }
  }, [anearEvent])
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (anearEvent) {
      CssLoader.ensureAppCss(anearEvent)
    }
    return () => {
      if (anearEvent) {
        CssLoader.removeAppCss(anearEvent)
      }
    }
  }, [anearEvent])

  useEffect(() => {
    // ANEAR EVENT TEARDOWN
    return () => {
      if (cleanupRef.current) return // Prevent multiple cleanups
      cleanupRef.current = true

      const cleanup = async () => {
        try {
          // Small delay to allow for immediate remount scenarios (like browser refresh)
          await new Promise(resolve => setTimeout(resolve, 100))

          const parentLeaveType =
            (eventRef.current && isDeadRouteState(eventRef.current.state))
              ? 'PARTICIPANT_EXIT'
              : EventUnmountMessageType

          // Propagate exit to all child events
          // Find all anear-app elements in the DOM (they may be in shadow DOMs)
          try {
            const anearAppElements = document.querySelectorAll('anear-app')
            for (const element of anearAppElements) {
              if (typeof element.propagateExit === 'function') {
                await element.propagateExit(parentLeaveType)
              }
            }
          } catch (err) {
            logger.error('Error propagating exit to child events during teardown', err)
          }

          if (eventRef.current) {
            await eventRef.current.closeMessaging()
          }
          if (anearParticipant) {
            await anearParticipant.leaveEvent(parentLeaveType)
          }
        } catch (err) {
          logger.error('Error closing event messaging', err)
        }
      }

      cleanup()
    } // eslint-disable-next-line
  }, []) // Remove anearParticipant dependency

  useEffect(() => {
    if (!anearParticipant) return
    anearParticipant.setUrlContext(urlContext)
  }, [anearParticipant, urlContext])

  // Allow re-subscribe after reloadEvent (new AnearEvent instance, same route).
  useEffect(() => {
    subscribedRef.current = false
  }, [anearEvent?.id])

  useEffect(() => {
    // ANEAR EVENT ABLY SUBSCRIPTIONS
    if (!anearEvent || subscribedRef.current) return
    if (!anearEvent.isInProgress()) return

    subscribedRef.current = true
    anearEvent.subscribeEventBroadcastMessages(broadcastHandler)
    anearEvent.subscribeEventQuiescedMessages(eventQuiescedHandler)

  }, [anearEvent, broadcastHandler, eventQuiescedHandler])

  useEffect(() => {
    // Auto-join participants for:
    // 1. Announce events that do not allow spectators (legacy behavior)
    // 2. Open-house events ONLY when spectators are not allowed (so there is no "Lobby/JOIN" choice)
    // when entering via /e/:slug.
    if (!anearEvent || loadingEvent || loadingParticipant || autoJoinAttempted) return
    if (anearParticipant) return

    const isOpenHouse = typeof anearEvent.isOpenHouse === 'function'
      ? anearEvent.isOpenHouse()
      : false
    const isAnnounceState = anearEvent.state === 'announce'
    const isLiveState = anearEvent.state === 'live'
    const isCreatedState = anearEvent.state === 'created'
    const isHostControlAutoJoinState = HostControlAutoJoinStates.includes(anearEvent.state)
    const allowsSpectators = anearEvent.spectatorsAllowed()
    const isHostedControlUser =
      anearEvent.hosted &&
      typeof anearEvent.supportsHostControl === 'function' &&
      anearEvent.supportsHostControl() &&
      (
        (typeof anearEvent.isCreator === 'function' && anearEvent.isCreator()) ||
        (typeof anearEvent.isParticible === 'function' && anearEvent.isParticible())
      )
    // Zone event hosts (particible) must join as participants to attach to the actions channel.
    // When spectators are disallowed there is no lobby/spectator path — without auto-join they spin forever.
    const isParticibleHostNoSpectatorsAutoJoin =
      !allowsSpectators &&
      typeof anearEvent.isParticible === 'function' &&
      anearEvent.isParticible() &&
      (isCreatedState || isAnnounceState || isLiveState)

    // Auto-join conditions:
    // 1. Open-house events: auto-join ONLY when spectators are not allowed, in announce or live.
    //    (If spectators ARE allowed, keep the user in spectator mode until they click JOIN.)
    // 2. Non-open-house, non-spectator events: auto-join in announce state (legacy behavior)
    //    (non-spectator events need participants to join early)
    const shouldAutoJoin = (isHostedControlUser && isHostControlAutoJoinState) ||
                          isParticibleHostNoSpectatorsAutoJoin ||
                          (isOpenHouse && !allowsSpectators && (isAnnounceState || isLiveState)) ||
                          (!isOpenHouse && !allowsSpectators && isAnnounceState)

    if (!shouldAutoJoin) return

    let cancelled = false

    const attemptAutoJoin = async () => {
      setAutoJoinAttempted(true)
      try {
        await becomeParticipant()
        // Clear any previous participant errors on successful join
        setParticipantError(null)
      } catch (err) {
        if (!cancelled) {
          logger.error('Auto-join as participant failed', err)
          setError(err)
          setParticipantError(err)
        }
      }
    }

    attemptAutoJoin()

    return () => {
      cancelled = true
    }
  }, [
    anearEvent,
    anearParticipant,
    loadingEvent,
    loadingParticipant,
    autoJoinAttempted,
    becomeParticipant,
    setError,
  ])

  const cancelClickHandler = async (anearEvent, leaveType = ParticipantExitType) => {
    logger.debug("canceling event ", anearEvent.id)

    // Propagate exit to all child events
    // Find all anear-app elements in the DOM (they may be in shadow DOMs)
    try {
      const anearAppElements = document.querySelectorAll('anear-app')
      for (const element of anearAppElements) {
        if (typeof element.propagateExit === 'function') {
          await element.propagateExit(leaveType)
        }
      }
    } catch (err) {
      logger.error('Error propagating exit to child events', err)
    }

    if (anearParticipant) {
      await anearParticipant.leaveEvent(leaveType)
    }

    const fromPath = location?.state?.from
    const shouldReturnToFromPath =
      leaveType === EventUnmountMessageType &&
      typeof fromPath === 'string' &&
      fromPath.startsWith('/')

    if (shouldReturnToFromPath) {
      navigateToPathWithReplace(fromPath, false)
      return
    }

    navigateToHome()
  }

  const exitClickHandler = async (opts = {}) => {
    const currentState = anearEvent?.state
    const isSpectator = !anearParticipant
    // Defaults:
    // - spectators: no prompt
    // - non-playable states: no prompt
    // - playable states (announce/live): prompt for participants/hosts
    //
    // Designers can override via <anear-nav prompt> / <anear-nav no_prompt>
    const defaultShouldPrompt =
      !isSpectator && (currentState === 'announce' || currentState === 'live')
    const hasPromptOverride =
      opts?.prompt !== undefined && opts?.prompt !== null && opts?.prompt !== false
    const shouldConfirmExit =
      opts?.noPrompt ? false : (hasPromptOverride ? true : defaultShouldPrompt)

    if (shouldConfirmExit) {
      setExitConfirmState({
        open: true,
        prompt: typeof opts?.prompt === 'string' ? opts.prompt : null,
        stayLabel: opts?.stayLabel || null,
        exitLabel: opts?.exitLabel || null,
      })
      return
    }

    cancelClickHandler(anearEvent)
  }

  const eventParticipantFailure = error => {
    logger.error(error)
    setError(error)
    setParticipantError(error)
  }

  const eventParticipantHandler = async () => {
    try {
      await becomeParticipant()
      // Clear any previous participant errors on successful join
      setParticipantError(null)
    } catch (errMsg) {
      eventParticipantFailure(errMsg)
    }
  }

  const eventClickHandlers = async (e, command, opts = {}) => {
    await browserClickHandlers(e, command, anearEvent, opts)
  }

  const browserClickHandlers = async (e, action, anearEvent = null, opts = {}) => {
    switch (action) {
      case "cancel":
        // Cancel is treated as explicit hard exit.
        cancelClickHandler(anearEvent)
        break
      case "back":
        // Back should be a non-fatal disconnect (maps to PARTICIPANT_DISCONNECT in AppM).
        cancelClickHandler(anearEvent, EventUnmountMessageType)
        break
      case "exit":
        await exitClickHandler(opts)
        break
      case "replay":
        // restore clone event and play again logic here
        break
      case "join":
        await eventParticipantHandler()
        break
      default:
        throw new Error(`unknown browserClick command=${action}`)
    }
  }

  useLayoutEffect(() => {
    const shortName = anearEvent?.zone?.app?.shortName
    if (typeof shortName === 'string' && shortName.trim()) {
      setDocumentTitleLayer('event', shortName)
    } else {
      clearDocumentTitleLayer('event')
    }
    return () => clearDocumentTitleLayer('event')
  }, [anearEvent])

  const currentEventState = anearEvent?.state

  if (loadingEvent) return null

  if (!loadingEvent && !anearEvent) {
    // If the event slug does not resolve (404), show the same
    // "event message" experience we use for completed events.
    const status = eventError && (eventError.status || eventError.error?.status)
    if (status && Number(status) === 404) {
      return (
        <EventOverPage
          anearEvent={null}
          customMessage="Sorry, this event is no longer available."
        />
      )
    }

    // Major/minor (or route-down) mismatch: finite message, not an infinite blue spinner.
    if (isAppVersionIncompatibleError(eventError)) {
      return (
        <EventOverPage
          anearEvent={null}
          customMessage={
            eventError.message ||
            'This event’s app was upgraded and can no longer be loaded. Please start a new event.'
          }
        />
      )
    }

    // For all other unexpected errors, fall back to the Events list.
    return <Navigate to={ROUTES.EVENTS} replace />
  }

  // Check if dead-route state BEFORE loading participant.
  if (isDeadRouteState(currentEventState)) {
    return <EventOverPage anearEvent={anearEvent} />
  }

  if ((anearEvent?.isParticipant() || anearEvent?.isCreator()) && loadingParticipant) return null

  const concludedWithoutViewerPath =
    currentEventState === 'concluded' &&
    !anearEvent.spectatorsAllowed() &&
    !(
      anearEvent.hosted &&
      (
        (typeof anearEvent.isCreator === 'function' && anearEvent.isCreator()) ||
        (typeof anearEvent.isParticible === 'function' && anearEvent.isParticible()) ||
        anearParticipant?.isHost?.()
      )
    )

  if (concludedWithoutViewerPath) {
    return <EventOverPage anearEvent={anearEvent} />
  }

  if (bootReason) {
    return <EventOverPage anearEvent={anearEvent} customMessage={bootReason} />
  }

  if (quiesceInfo) {
    return (
      <EventPausedPage
        anearEvent={anearEvent}
        reason={quiesceInfo.reason}
        reconnectLabel={quiesceInfo.reconnectLabel}
        showRejoin={!!participantRef.current}
      />
    )
  }

  // Display geolocation and other participant join errors using EventOverPage
  // This includes errors from becomeEventParticipant (which may include geolocation errors)
  const errorToCheck = participantError || eventError
  if (errorToCheck) {
    const errorMessage = errorToCheck.message || errorToCheck.toString()
    // Check if this is a geolocation-related error
    if (errorMessage.includes('geolocation') || errorMessage.includes('Geolocation') ||
        errorMessage.includes('Unable to obtain geolocation') ||
        errorMessage.includes('Location') ||
        errorMessage.includes('location')) {
      return (
        <EventOverPage
          anearEvent={anearEvent}
          customMessage={errorMessage.includes('Unable to obtain geolocation')
            ? errorMessage
            : 'Unable to obtain geolocation. Please try your event request again.'}
        />
      )
    }
    // For other errors during participant join that prevent joining, show EventOverPage
    if (errorMessage.includes('required') || errorMessage.includes('Required') ||
        errorMessage.includes('proceed')) {
      return (
        <EventOverPage
          anearEvent={anearEvent}
          customMessage={errorMessage}
        />
      )
    }
  }

  return (
    <Box
      sx={{
        width: 'auto',
        display: 'block',
        alignItems: 'center',
        flexDirection: 'column',
        px: 0,
        py: 0
      }}
    >
      <EventConfirmExitDialog
        open={exitConfirmState.open}
        anearEvent={anearEvent}
        prompt={exitConfirmState.prompt}
        stayLabel={exitConfirmState.stayLabel}
        exitLabel={exitConfirmState.exitLabel}
        onStay={() => setExitConfirmState({
          open: false,
          prompt: null,
          stayLabel: null,
          exitLabel: null,
        })}
        onExit={() => {
          setExitConfirmState({
            open: false,
            prompt: null,
            stayLabel: null,
            exitLabel: null,
          })
          cancelClickHandler(anearEvent)
        }}
      />
      <EventFrame
        anearEvent={anearEvent}
        eventState={anearEvent.state}
        eventClickHandlers={eventClickHandlers}
        anearParticipant={anearParticipant}
        bootParticipantHandler={bootParticipantHandler}
      />
    </Box>
  )
}

export default EventPage
